import { Popover, Stack, Typography, useTheme } from '@mui/material'
import { type FC, type ReactNode, useState } from 'react'

interface SettingsSentenceProps {
    parts: SentencePart[]
}

type SentencePart =
    | { type: 'text'; content: string }
    | { type: 'bold'; content: string }
    | { type: 'interactive'; label: ReactNode; popoverContent: ReactNode }

export const SettingsSentence: FC<SettingsSentenceProps> = ({ parts }) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const handleClick = (
        event: React.MouseEvent<HTMLElement>,
        index: number
    ) => {
        setAnchorEl(event.currentTarget)
        setActiveIndex(index)
    }

    const handleClose = () => {
        setAnchorEl(null)
        setActiveIndex(null)
    }

    return (
        <Typography variant="caption" color="text.secondary">
            {parts.map((part, index) => {
                if (part.type === 'text') {
                    return <span key={index}>{part.content}</span>
                }
                if (part.type === 'bold') {
                    return <b key={index}>{part.content}</b>
                }
                if (part.type === 'interactive') {
                    return (
                        <span key={index}>
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleClick(e, index)}
                                onKeyDown={(e) =>
                                    e.key === 'Enter' &&
                                    handleClick(
                                        e as unknown as React.MouseEvent<HTMLElement>,
                                        index
                                    )
                                }
                                style={{
                                    fontWeight: 'bold',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    cursor: 'pointer',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {part.label}
                            </span>
                            <Popover
                                open={activeIndex === index}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                slotProps={{
                                    paper: {
                                        sx: { p: 2, mt: 0.5 },
                                    },
                                }}
                            >
                                <Stack gap={1}>{part.popoverContent}</Stack>
                            </Popover>
                        </span>
                    )
                }
                return null
            })}
        </Typography>
    )
}

export type { SentencePart }
